import { Input, Label, Field, Radio, RadioGroup } from '@headlessui/react';
import { faCheckCircle, faChevronUp, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ProductType, SaleAs, IProductTypeDetails } from '../../../__generated__/graphql';
import SwitchComponent from '../../components/switch';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ALL_PRODUCT_TYPE_DETAILS,
  UPDATE_PRODUCT_TYPE_DETAILS,
  CREATE_PRODUCT_TYPE_DETAILS,
} from '../../services/productTypeDetailsService/queries';
import { toast } from 'react-toastify';
import { PRODUCT_TYPE_OPTIONS } from '../../constants/productTypeOptions';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border px-4 pb-8 md:pb-16`}
`;

const ContentContainer = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl max-w-full min-w-0 box-border`}
`;

const StyledRadioGroup = styled(RadioGroup)`
  ${tw`space-y-2`}
`;

const StyledRadio = styled(Radio)`
  ${tw`relative flex cursor-pointer rounded-lg bg-bg-secondary py-4 px-5 text-white shadow-card transition`}
  &:focus {
    ${tw`outline-1 outline-red-500`}
  }
  &[data-checked] {
    ${tw`bg-black/10`}
  }
`;

const RadioContent = styled.div`
  ${tw`flex w-full items-center justify-between`}
`;

const PlanInfo = styled.div`
  ${tw`text-sm/6`}
`;

const PlanName = styled.p`
  ${tw`font-semibold text-text-primary`}
`;

const PlanDetails = styled.div`
  ${tw`flex gap-2 text-text-primary`}
`;

const Separator = styled.div`
  &::before {
    content: '·';
  }
`;

const StyledLabel = styled(Label)`
  ${tw`text-sm/6 font-medium text-text-primary`}
`;

const StyledInput = styled.input`
  ${tw`mt-3 w-full rounded-lg border-none bg-bg-secondary text-sm/6 text-text-primary h-[44px]`}
  &:focus {
    ${tw`outline-none`}
  }
  &[data-focus] {
    ${tw`outline-2 -outline-offset-2 outline-white/25`}
  }
`;

const StyledSelect = styled.select`
  ${tw`mt-3 w-full rounded-lg border-none bg-bg-secondary text-sm/6 text-text-primary h-[44px]`}
  &:focus {
    ${tw`outline-none`}
  }
  &[data-focus] {
    ${tw`outline-2 -outline-offset-2 outline-white/25`}
  }
`;

const InputField = styled(Field)<{ inVertical?: boolean }>`
  ${tw`max-w-full bg-black/10 p-4 flex flex-col`}
  ${({ inVertical = true }) => !inVertical && tw`flex flex-row justify-between`}
`;

const InputContainer = styled.div`
  ${tw`max-w-full mt-4 bg-bg-secondary`}
`;

const DeSelectButton = styled.button`
  ${tw`w-full h-[30px] border-none bg-black/10`}
  &:hover {
    ${tw`bg-black/20 cursor-pointer`}
  }
`;

const StyledButton = styled.button`
  ${tw`gap-10 w-full cursor-pointer rounded-md bg-black px-4 py-3 text-center text-sm font-semibold uppercase text-white transition duration-200 ease-in-out border-none`}
  &:hover {
    ${tw`bg-green-700`}
  }
`;

const StyledTitle = styled.h1`
  ${tw`text-3xl font-bold text-text-primary mt-10 mb-6`}
`;

interface ProductTypeDetailsData {
  productTypeDetails: IProductTypeDetails[];
}

export default function ProductTypeDetailsPage() {
  const [selected, setSelected] = useState<IProductTypeDetails | null>(null);
  const radioGroupRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, refetch } = useQuery<ProductTypeDetailsData>(
    GET_ALL_PRODUCT_TYPE_DETAILS,
  );

  const plansFromDb = data?.productTypeDetails || [];

  // Merge PRODUCT_TYPE_OPTIONS with data from DB
  const plans = PRODUCT_TYPE_OPTIONS.filter(opt => opt.value !== ProductType.All).map(option => {
    const existing = plansFromDb.find(p => p.type === option.value);
    if (existing) return existing;

    // Return a template for product types not yet in DB
    return {
      type: option.value,
      saleAs: SaleAs.Rental,
      hasInsurance: true,
      hasTripFee: true,
      hasYoungsterFee: true,
      taxable: true,
      isOn: false,
    } as IProductTypeDetails;
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        radioGroupRef.current &&
        !radioGroupRef.current.contains(event.target as Node) &&
        selected !== null
      ) {
        setSelected(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selected]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <PageContainer>
      <ContentContainer>
        <StyledTitle>Production Types Set up</StyledTitle>
        <StyledRadioGroup
          ref={radioGroupRef}
          value={selected}
          aria-label="Product Type Details"
          as="div"
        >
          {plans.map(plan => (
            <div key={plan.id || plan.type}>
              <EachPlan
                plan={plan}
                isSelected={selected?.id === plan.id || selected?.type === plan.type}
                onDeselect={() => setSelected(null)}
                setSelected={setSelected}
                refetch={refetch}
              />
            </div>
          ))}
        </StyledRadioGroup>
      </ContentContainer>
    </PageContainer>
  );
}

export function EachPlan({
  plan,
  isSelected,
  onDeselect,
  setSelected,
  refetch,
}: {
  plan: IProductTypeDetails;
  isSelected: boolean;
  onDeselect: () => void;
  setSelected: (plan: IProductTypeDetails) => void;
  refetch: () => Promise<any>;
}) {
  const [isOn, setIsOn] = useState(plan.isOn !== undefined ? !!plan.isOn : true);
  const [saleAs, setSaleAs] = useState<SaleAs>(plan.saleAs || SaleAs.Rental);
  const [hasInsurance, setHasInsurance] = useState(
    plan.hasInsurance !== undefined ? !!plan.hasInsurance : true,
  );
  const [hasTripFee, setHasTripFee] = useState(
    (plan as any).hasTripFee !== undefined ? !!(plan as any).hasTripFee : true,
  );
  const [hasYoungsterFee, setHasYoungsterFee] = useState(
    (plan as any).hasYoungsterFee !== undefined ? !!(plan as any).hasYoungsterFee : true,
  );
  const [taxable, setTaxable] = useState(plan.taxable !== undefined ? !!plan.taxable : true);
  const [currentPlan, setCurrentPlan] = useState<IProductTypeDetails>(plan);

  useEffect(() => {
    setCurrentPlan(plan);
    setIsOn(plan.isOn !== undefined ? !!plan.isOn : true);
    setSaleAs(plan.saleAs || SaleAs.Rental);
    setHasInsurance(plan.hasInsurance !== undefined ? !!plan.hasInsurance : true);
    setHasTripFee((plan as any).hasTripFee !== undefined ? !!(plan as any).hasTripFee : true);
    setHasYoungsterFee(
      (plan as any).hasYoungsterFee !== undefined ? !!(plan as any).hasYoungsterFee : true,
    );
    setTaxable(plan.taxable !== undefined ? !!plan.taxable : true);
  }, [plan]);

  const [createProductTypeDetails, { loading: isCreating }] = useMutation(
    CREATE_PRODUCT_TYPE_DETAILS,
    {
      refetchQueries: [{ query: GET_ALL_PRODUCT_TYPE_DETAILS }],
      awaitRefetchQueries: true,
      onCompleted: async () => {
        toast.success('Product type details created successfully');
        await refetch();
      },
      onError: () => {
        toast.error('Failed to create product type details');
      },
    },
  );

  const [updateProductTypeDetails, { loading: isUpdating }] = useMutation(
    UPDATE_PRODUCT_TYPE_DETAILS,
    {
      refetchQueries: [{ query: GET_ALL_PRODUCT_TYPE_DETAILS }],
      awaitRefetchQueries: true,
      onCompleted: async data => {
        toast.success('Product type details updated successfully');
        await refetch();
        if (data.updateProductTypeDetails && currentPlan.id === data.updateProductTypeDetails.id) {
          setCurrentPlan(data.updateProductTypeDetails as IProductTypeDetails);
          setSelected(data.updateProductTypeDetails as IProductTypeDetails);
        }
      },
      onError: () => {
        toast.error('Failed to update product type details');
      },
    },
  );

  const handleSave = () => {
    if (!plan.id) {
      // Create: include type
      const createInput = {
        type: plan.type,
        saleAs: saleAs ?? SaleAs.Rental,
        hasInsurance: hasInsurance ?? true,
        hasTripFee: hasTripFee ?? true,
        hasYoungsterFee: hasYoungsterFee ?? true,
        taxable: taxable ?? true,
        isOn: isOn ?? true,
      };
      createProductTypeDetails({
        variables: {
          input: createInput,
        },
      });
    } else {
      // Update: exclude type (type cannot be changed)
      const updateInput = {
        saleAs: saleAs,
        hasInsurance: hasInsurance,
        hasTripFee: hasTripFee,
        hasYoungsterFee: hasYoungsterFee,
        taxable: taxable,
        isOn: isOn,
      };
      updateProductTypeDetails({
        variables: {
          id: plan.id,
          input: updateInput,
        },
      });
    }
  };

  const typeLabel = PRODUCT_TYPE_OPTIONS.find(opt => opt.value === plan.type)?.label || plan.type;

  return (
    <>
      <StyledRadio
        key={plan.id || plan.type}
        value={plan}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setSelected(plan);
        }}
        as="div"
      >
        <RadioContent as="div">
          <PlanInfo>
            <PlanName>{typeLabel}</PlanName>
            <PlanDetails>
              <div>Sale as: {saleAs}</div>
              <Separator aria-hidden="true" />
              <div>
                updated on: {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : 'N/A'}
              </div>
              <Separator aria-hidden="true" />
              <div>{isOn ? 'Active' : 'Inactive'}</div>
            </PlanDetails>
          </PlanInfo>
          {isOn ? (
            <FontAwesomeIcon icon={faCheckCircle} color="green" />
          ) : (
            <FontAwesomeIcon icon={faMinusCircle} color="red" />
          )}
        </RadioContent>
      </StyledRadio>
      {isSelected ? (
        <InputContainer>
          <DeSelectButton onClick={onDeselect}>
            <FontAwesomeIcon icon={faChevronUp} size="xl" color="darkgreen" />
          </DeSelectButton>
          <InputField inVertical={false}>
            <StyledLabel>Active (isOn)</StyledLabel>
            <SwitchComponent isActive={!!isOn} onChange={() => setIsOn(!isOn)} color="green" />
          </InputField>
          <InputField>
            <StyledLabel>Sale As</StyledLabel>
            <StyledSelect
              value={saleAs}
              onChange={e => {
                setSaleAs(e.target.value as SaleAs);
              }}
            >
              <option value={SaleAs.Rental}>Rental</option>
              <option value={SaleAs.Service}>Service</option>
              <option value={SaleAs.OneTime}>One Time</option>
            </StyledSelect>
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Has protection plan</StyledLabel>
            <SwitchComponent
              isActive={!!hasInsurance}
              onChange={() => setHasInsurance(!hasInsurance)}
              color="green"
            />
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Has Trip Fee</StyledLabel>
            <SwitchComponent
              isActive={!!hasTripFee}
              onChange={() => setHasTripFee(!hasTripFee)}
              color="green"
            />
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Has Youngster Fee</StyledLabel>
            <SwitchComponent
              isActive={!!hasYoungsterFee}
              onChange={() => setHasYoungsterFee(!hasYoungsterFee)}
              color="green"
            />
          </InputField>
          <InputField inVertical={false}>
            <StyledLabel>Taxable</StyledLabel>
            <SwitchComponent
              isActive={!!taxable}
              onChange={() => setTaxable(!taxable)}
              color="green"
            />
          </InputField>

          <StyledButton onClick={handleSave} disabled={isCreating || isUpdating}>
            Save
          </StyledButton>
        </InputContainer>
      ) : null}
    </>
  );
}
